import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import AppLayout from "@/layouts/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import CustomersPage from "@/pages/CustomersPage";
import SalesPage from "@/pages/SalesPage";
import FollowUpsPage from "@/pages/FollowUpsPage";
import TasksPage from "@/pages/TasksPage";
import UsersPage from "@/pages/UsersPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import ProductsPage from "@/pages/ProductsPage";
import BulkImportPage from "@/pages/BulkImportPage";
import IngredientsPage from "@/pages/IngredientsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import VendorPurchasesPage from "@/pages/VendorPurchasesPage";
import HOReportsPage from "@/pages/HOReportsPage";
import AccountsPage from "@/pages/AccountsPage";
import QCPage from "@/pages/QCPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import HRPage from "@/pages/HRPage";
import RecipesPage from "@/pages/RecipesPage";
import RolesPage from "@/pages/RolesPage";
import POSPage from "@/pages/POSPage";
import BranchesPage from "@/pages/BranchesPage";
import ManufacturingPage from "@/pages/ManufacturingPage";
import FactoryDashboardPage from "@/pages/FactoryDashboardPage";
import ExecutiveDashboardPage from "@/pages/ExecutiveDashboardPage";
import HODprSyncPage from "@/pages/HODprSyncPage";
import BIDashboardPage from "@/pages/BIDashboardPage";
import YieldAnalysisPage from "@/pages/YieldAnalysisPage";
import TodaysDealsPage from "@/pages/TodaysDealsPage";
import DealsConfigPage from "@/pages/DealsConfigPage";
import FactoryAdminsPage from "@/pages/FactoryAdminsPage";
import FactoriesPage from "@/pages/FactoriesPage";
import ShowroomsPage from "@/pages/ShowroomsPage";
import ShowroomMonitorPage from "@/pages/ShowroomMonitorPage";
import BatchTracePage from "@/pages/BatchTracePage";
import HoPlannerPage from "@/pages/HoPlannerPage";
import WarehouseDispatchPage from "@/pages/WarehouseDispatchPage";
import StoreDashboardPage from "@/pages/StoreDashboardPage";
import GRNPage from "@/pages/GRNPage";
import RecipesAndSkusPage from "@/pages/RecipesAndSkusPage";
import MobileStorePage from "@/pages/MobileStorePage";
import AuditLogPage from "@/pages/AuditLogPage";
import MarkdownCampaignsPage from "@/pages/MarkdownCampaignsPage";
import LoyaltyPage from "@/pages/LoyaltyPage";
import ShopApp from "@/shop/ShopApp";
import ShopAdminPage from "@/pages/ShopAdminPage";
import B2BCorporatePage from "@/pages/B2BCorporatePage";
import WasteDamagePage from "@/pages/WasteDamagePage";
import DisposalsPage from "@/pages/DisposalsPage";
import ForecastingPage from "@/pages/ForecastingPage";

// Mobile PWA
import MobileLayout from "@/mobile/MobileLayout";
import MobileLogin from "@/mobile/MobileLogin";
import MobileHome from "@/mobile/MobileHome";
import MobileCheckin from "@/mobile/MobileCheckin";
import MobileDeliveries from "@/mobile/MobileDeliveries";
import MobileDeliveryDetail from "@/mobile/MobileDeliveryDetail";
import MobileOwner from "@/mobile/MobileOwner";
import ForcePasswordChange from "@/pages/ForcePasswordChange";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#8B7355]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
  return children;
}

// Hard route guard for branch-scoped roles — locks them out of HO/global admin pages.
function BranchScopedGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role_id || user?.role;
  const isFactoryScoped = role === "factory_manager" || role === "factory_admin";
  const isShowroomScoped = role === "showroom_admin" || role === "showroom_sales_manager";
  const isQCScoped = role === "qc_manager" || role === "qc_staff";
  const isBranchScoped = isFactoryScoped || isShowroomScoped || isQCScoped;
  // QC role locking: whitelist — QC users may ONLY visit /app/qc and its sub-routes.
  if (isQCScoped) {
    if (!location.pathname.startsWith("/app/qc")) {
      return <Navigate to="/app/qc" replace />;
    }
    return children;
  }
  // Common HO/global admin surface — blocked for ALL branch-scoped roles.
  const ADMIN_BLOCKED = [
    "/app/users", "/app/roles", "/app/settings", "/app/audit-log",
    "/app/factories", "/app/factory-admins",
    "/app/showrooms", "/app/showroom-monitor",
    "/app/ho-planner", "/app/masters/recipes-and-skus",
    "/app/branches", "/app/accounts",
    "/app/leads", "/app/loyalty", "/app/shop-admin",
    "/app/deals", "/app/mobile-store",
    "/app/forecasting", "/app/followups", "/app/tasks",
    "/app/markdown-campaigns", "/app/notifications",
    "/app/mfg/executive-dashboard", "/app/dashboard",
  ];
  // Factory personas additionally may NOT visit retail/customer surfaces.
  const FACTORY_EXTRA_BLOCKED = [
    "/app/pos", "/app/sales", "/app/customers", "/app/hr",
    "/app/grn", "/app/store-dashboard",
  ];
  // Showroom personas additionally may NOT visit factory/manufacturing surfaces.
  const SHOWROOM_EXTRA_BLOCKED = [
    "/app/manufacturing", "/app/mfg/factory-dashboard",
    "/app/mfg/yield-analysis", "/app/qc",
    "/app/purchase-orders", "/app/ingredients", "/app/recipes",
    "/app/warehouse-dispatch",
    // Per user spec: showroom staff see ONLY POS, Stock and GRN
    "/app/sales", "/app/products", "/app/hr", "/app/customers",
  ];
  const blocked = new Set([
    ...ADMIN_BLOCKED,
    ...(isFactoryScoped ? FACTORY_EXTRA_BLOCKED : []),
    ...(isShowroomScoped ? SHOWROOM_EXTRA_BLOCKED : []),
  ]);
  if (isBranchScoped && blocked.has(location.pathname)) {
    const home = isShowroomScoped ? "/app/store-dashboard" : "/app/mfg/factory-dashboard";
    return <Navigate to={home} replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/q/:token" element={<PublicQuotePage />} />
          <Route path="/deals" element={<TodaysDealsPage />} />
          <Route path="/deals/:branchCode" element={<TodaysDealsPage />} />
          <Route path="/qr/batch/:id" element={<BatchTracePage />} />
          <Route path="/shop/*" element={<ShopApp />} />

          {/* Mobile PWA */}
          <Route path="/m" element={<Navigate to="/m/login" replace />} />
          <Route path="/m/login" element={<MobileLogin />} />
          <Route path="/m" element={<MobileLayout />}>
            <Route path="home" element={<MobileHome />} />
            <Route path="checkin" element={<MobileCheckin />} />
            <Route path="deliveries" element={<MobileDeliveries />} />
            <Route path="deliveries/:id" element={<MobileDeliveryDetail />} />
            <Route path="owner" element={<MobileOwner />} />
          </Route>

          <Route
            path="/app"
            element={
              <Protected>
                <BranchScopedGuard>
                  <AppLayout />
                </BranchScopedGuard>
              </Protected>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="pos" element={<POSPage />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="manufacturing" element={<ManufacturingPage />} />
            <Route path="mfg/factory-dashboard" element={<FactoryDashboardPage />} />
            <Route path="mfg/executive-dashboard" element={<ExecutiveDashboardPage />} />
            <Route path="mfg/ho-dpr-sync" element={<HODprSyncPage />} />
            <Route path="bi" element={
              <Protected roles={["super_admin", "admin", "chief_accountant"]}>
                <BIDashboardPage />
              </Protected>
            } />
            <Route path="mfg/yield-analysis" element={<YieldAnalysisPage />} />
            <Route path="deals" element={<DealsConfigPage />} />
            <Route path="factory-admins" element={<FactoryAdminsPage />} />
            <Route path="factories" element={<FactoriesPage />} />
            <Route path="showrooms" element={<ShowroomsPage />} />
            <Route path="showroom-monitor" element={<ShowroomMonitorPage />} />
            <Route path="ho-planner" element={<HoPlannerPage />} />
            <Route path="warehouse-dispatch" element={<WarehouseDispatchPage />} />
            <Route path="store-dashboard" element={<StoreDashboardPage />} />
            <Route path="grn" element={<GRNPage />} />
            <Route path="masters/recipes-and-skus" element={<RecipesAndSkusPage />} />
            <Route path="mobile-store" element={<MobileStorePage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="vendor-purchases" element={<VendorPurchasesPage />} />
            <Route path="ho-reports" element={<HOReportsPage />} />
            <Route path="disposals" element={<DisposalsPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route
              path="products/bulk-import"
              element={
                <Protected roles={["super_admin", "admin"]}>
                  <BulkImportPage />
                </Protected>
              }
            />
            <Route path="ingredients" element={<IngredientsPage />} />
            <Route path="recipes" element={<RecipesPage />} />
            <Route path="qc" element={<QCPage />} />
            <Route path="followups" element={<FollowUpsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="markdown-campaigns" element={<MarkdownCampaignsPage />} />
            <Route path="loyalty" element={<LoyaltyPage />} />
            <Route path="shop-admin" element={<ShopAdminPage />} />
            <Route
              path="b2b-corporate"
              element={
                <Protected roles={["super_admin", "admin", "chief_accountant", "ho_admin", "accountant"]}>
                  <B2BCorporatePage />
                </Protected>
              }
            />
            <Route
              path="waste-damage"
              element={
                <Protected roles={["super_admin", "admin", "chief_accountant", "ho_admin", "factory_manager", "factory_admin", "production_manager", "qc_manager"]}>
                  <WasteDamagePage />
                </Protected>
              }
            />
            <Route path="forecasting" element={<ForecastingPage />} />
            <Route path="hr" element={<HRPage />} />
            <Route
              path="accounts"
              element={
                <Protected roles={["super_admin", "admin", "accountant", "chief_accountant", "assistant_accountant"]}>
                  <AccountsPage />
                </Protected>
              }
            />
            <Route path="reports" element={<ReportsPage />} />
            <Route
              path="users"
              element={
                <Protected roles={["super_admin", "admin"]}>
                  <UsersPage />
                </Protected>
              }
            />
            <Route
              path="roles"
              element={
                <Protected roles={["super_admin"]}>
                  <RolesPage />
                </Protected>
              }
            />
            <Route
              path="audit-log"
              element={
                <Protected roles={["super_admin", "admin"]}>
                  <AuditLogPage />
                </Protected>
              }
            />
            <Route
              path="settings"
              element={
                <Protected roles={["super_admin", "admin"]}>
                  <SettingsPage />
                </Protected>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
